import type {
  Partner,
  PartnerAddress,
  PartnerContact,
} from "@/app/api/types/partner";

export interface PartnerContactSummary {
  name: string | null;
  phone: string | null;
  email: string | null;
}

const firstTrimmedNonEmpty = (
  xs: readonly (string | null | undefined)[] | null | undefined,
): string | null => {
  if (!xs) return null;
  for (const x of xs) {
    if (x && x.trim().length > 0) return x.trim();
  }
  return null;
};

export const getPrimaryAddress = (
  partner: Partner | undefined,
): PartnerAddress | null => {
  const addresses = partner?.addresses;
  if (!addresses || addresses.length === 0) return null;
  return addresses.find((a) => a.is_default) ?? addresses[0];
};

export const getPrimaryContact = (
  partner: Partner | undefined,
): PartnerContact | null => {
  const contacts = partner?.contacts;
  if (!contacts || contacts.length === 0) return null;
  return (
    contacts.find((c) => c.company_owner) ??
    contacts.find((c) =>
      (c.phones ?? []).some((p) => !!p && p.trim().length > 0),
    ) ??
    contacts[0]
  );
};

export const getPartnerContactSummary = (
  partner: Partner | undefined,
): PartnerContactSummary | null => {
  const primary = getPrimaryContact(partner);
  if (!primary) return null;
  const fullName = `${primary.name ?? ""} ${primary.surname ?? ""}`.trim();
  return {
    name: fullName.length > 0 ? fullName : null,
    phone: firstTrimmedNonEmpty(primary.phones),
    email: firstTrimmedNonEmpty(primary.emails),
  };
};

export const formatPrimaryAddressOneLine = (
  partner: Partner | undefined,
): string | null => {
  const addr = getPrimaryAddress(partner);
  if (!addr) return null;
  const cityPart = [addr.zip_code, addr.city]
    .filter((p) => p && p.trim().length > 0)
    .join(" ");
  const parts = [cityPart, addr.address_1].filter(
    (p) => p && p.trim().length > 0,
  );
  const formatted = parts.join(", ").trim();
  return formatted.length > 0 ? formatted : null;
};

/**
 * Address split for space-constrained rows: the street is the part a display
 * may ellipsize, the tail (`"zip city, country"`) is the part that must stay
 * fully visible — two rows often share a name and differ only by locality.
 * Rendered by `EntityAddressLine` as `19 rue de …, 12345 Ville, FR`.
 */
export interface AddressParts {
  street: string | null;
  tail: string | null;
}

export const getPrimaryAddressParts = (
  partner: Partner | undefined,
): AddressParts | null => {
  const addr = getPrimaryAddress(partner);
  if (!addr) return null;
  const cityPart = [addr.zip_code, addr.city]
    .filter((p) => p && p.trim().length > 0)
    .join(" ");
  const tail = [cityPart, addr.country?.trim()]
    .filter((p): p is string => !!p && p.length > 0)
    .join(", ");
  const street = addr.address_1?.trim() || null;
  if (!street && tail.length === 0) return null;
  return { street, tail: tail.length > 0 ? tail : null };
};
